import { useCallback, useState } from 'react'
import { useToast } from '../contexts/ToastContext'
import { useAuth } from '../contexts/AuthContext'
import { createPayment, redirectToPayment } from '../services/paymentService'
import { clearSelectedPlan, storeSelectedPlan } from '../utils/selectedPlan'
import type { CheckoutPlanId } from '../data/pricingPlans'
interface UseCheckoutReturn {
  /** Frontend checkout action. Hands the plan identifier to the payment layer. */
  onCheckout: (plan: CheckoutPlanId) => Promise<void>
  pendingPlan: CheckoutPlanId | null
  /** Set when an unauthenticated visitor starts checkout. */
  authPromptPlan: CheckoutPlanId | null
  dismissAuthPrompt: () => void
}
/**
 * The single frontend checkout abstraction used by every paid CTA.
 * It performs no payment logic itself — it forwards the selected plan
 * identifier to the existing payment handler and follows the redirect.
 *
 * Deliberately router-free: the pricing section also renders on the
 * statically served marketing entry points.
 */
export function useCheckout(location = 'pricing'): UseCheckoutReturn {
  const { user } = useAuth()
  // Marketing entry points render without the toast provider.
  let toast: { showToast: ReturnType<typeof useToast>['showToast'] } | null =
    null
  try {
    toast = useToast()
  } catch {
    toast = null
  }
  const [pendingPlan, setPendingPlan] = useState<CheckoutPlanId | null>(null)
  const [authPromptPlan, setAuthPromptPlan] = useState<CheckoutPlanId | null>(
    null,
  )
  const onCheckout = useCallback(
    async (plan: CheckoutPlanId) => {
      // One checkout at a time — guards against duplicate requests.
      if (pendingPlan) return
      if (!user) {
        // Remember the selection so checkout resumes after authentication.
        storeSelectedPlan(plan)
        setAuthPromptPlan(plan)
        return
      }
      setPendingPlan(plan)
      try {
        const response = await createPayment(plan)
        clearSelectedPlan()
        redirectToPayment(response.authorization_url)
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : 'Checkout is unavailable right now. Please try again.'
        console.error(`Checkout failed (${location}):`, err)
        if (toast) {
          toast.showToast(message, 'error', 5000)
        } else {
          window.alert(`Checkout failed: ${message}`)
        }
        setPendingPlan(null)
      }
    },
    [location, pendingPlan, toast, user],
  )
  const dismissAuthPrompt = useCallback(() => setAuthPromptPlan(null), [])
  return {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt,
  }
}
